import type * as Types from '@aeppic/types'
import type { ParsedField } from '@aeppic/forms-parser'

import type Aeppic from 'aeppic/aeppic.js'
import { parseSelector } from '../lookup/form-oriented-lookup.js'

const CONTROL_FORM = 'control-form'
const DEFAULT_NAMESPACE = 'default'
const DEFAULT_TEXT_CONTROL = 'default-text-control'
const STANDALONE_CAPABLE = ['SUPPORTED', 'REQUIRED']

/** The two folders the legacy lookup checks, in its order. */
const LEGACY_CONTROL_FOLDERS: Types.DocumentId[] = [
  'controls-folder',
  '91f205bc-9989-4fbe-8966-bd4ba9777c26',
]

/**
 * The folder chains from `GET /api/ui-lookup-tables`.
 *
 * Only the chains are precomputed, not the answers: which control a placeholder resolves to
 * depends on the field's type and cardinality, and a field is a runtime thing. Shipping the
 * chain plus the control documents lets the client answer any placeholder without a request.
 */
export interface ControlLookupTables {
  controlFolderChainByForm: Record<string, Types.DocumentId[]>
}

/**
 * A document id, `null` for "searched, no such control", `undefined` for "cannot answer".
 *
 * The difference between the last two decides whether the caller may skip the network. Turning
 * `undefined` into `null` would answer for a form this client knows nothing about.
 */
export type ControlResolution = Types.DocumentId|null|undefined

/** Everything `findBestMatchingControlInFolder` filters on, and nothing else. */
interface ControlCandidate {
  id: Types.DocumentId
  namespace: string
  name: string
  cardinality: unknown
  standalone: unknown
  limitTypes: unknown
  types: unknown[]
  hasPackages: boolean
  createdAt: string
}

function buildCandidate(document: Types.Document): ControlCandidate {
  const data = document.data ?? {}

  return {
    id: document.id,
    namespace: typeof data.namespace === 'string' ? data.namespace : '',
    name: typeof data.name === 'string' ? data.name : '',
    cardinality: data.cardinality,
    standalone: data.standalone,
    limitTypes: data.limitTypes,
    types: Array.isArray(data.types) ? data.types : [],
    hasPackages: data.packages != null,
    createdAt: document.created?.at ?? '',
  }
}

/**
 * The type tokens a control must declare to accept this field.
 *
 * Mirrors the `types` list `findBestMatchingControlInFolder` builds.
 */
function supportedTypesFor(field: ParsedField): string[] {
  const types = field.subType === 'select' ? ['select_any', `select_${field.type}`] : [field.subType]

  if (field.type === 'string') {
    types.push('string')
  }

  return types
}

/**
 * Whether a candidate passes the same filters the server query applies.
 *
 * Every comparison is deliberately strict: `data.cardinality` is matched with `isOneOf`, so a
 * control that does not declare one is excluded rather than treated as universal, and
 * `data.limitTypes` is matched with `is(false)`, so only the literal `false` waives the type
 * check.
 */
function matchesField(candidate: ControlCandidate, field: ParsedField|null): boolean {
  if (!field) {
    return STANDALONE_CAPABLE.includes(candidate.standalone as string)
  }

  const allowedCardinalities = field.cardinality ? ['BOTH', 'MULTIPLE'] : ['BOTH', 'SINGLE']

  if (!allowedCardinalities.includes(candidate.cardinality as string)) {
    return false
  }

  if (candidate.limitTypes === false) {
    return true
  }

  return supportedTypesFor(field).some(type => candidate.types.includes(type))
}

/**
 * Split a selector the way the legacy lookup splits it.
 *
 * Including its quirk: the check is `controlName.indexOf(':')`, which is truthy for -1, so a
 * selector that *starts* with a colon is never split and is matched whole, colon included.
 * Names and namespaces are compared trimmed and lowercased there, unlike the current matcher.
 */
function parseLegacySelector(controlSelector: string) {
  let name = controlSelector
  let namespace = ''

  if (controlSelector.indexOf(':') !== 0) {
    const parts = controlSelector.split(':', 2)

    if (parts.length === 1) {
      [name] = parts
    } else {
      [namespace, name] = parts
    }
  }

  return {
    namespace: namespace.trim().toLowerCase(),
    name: name.trim().toLowerCase(),
  }
}

function matchesLegacyName(candidate: ControlCandidate, namespace: string, name: string): boolean {
  if (!candidate.hasPackages) {
    return false
  }

  return candidate.name.trim().toLowerCase() === name
      && candidate.namespace.trim().toLowerCase() === namespace
}

/**
 * Control resolution without a round trip.
 *
 * Resolving a control costs one search per folder in the form's chain, and every folder before
 * the hit answers nothing - 273 to 441 searches per form screen, 106 to 220 of them empty, plus
 * the ancestry walk that produces the chain. None of it depends on anything but the control
 * corpus, the folder chain and the field, so all three are shipped at boot and the walk happens
 * here.
 *
 * Both matchers live here, not just the current one. A miss today falls through to the legacy
 * lookup, which matches case-insensitively, retries in the `default` namespace and ends at
 * `default-text-control`; without that pass a local miss could not be trusted as final, and the
 * empty searches - the ones a local answer is meant to remove - would all still be made.
 */
export class Controls {
  private _folderChainByForm: Record<string, Types.DocumentId[]> = null
  private _candidatesByFolder: Map<Types.DocumentId, ControlCandidate[]> = null
  private _useLegacyFallback: boolean

  constructor(private _aeppic: Aeppic) {
    // The legacy pass is only part of today's answer when the feature says so, and reproducing
    // it unconditionally would resolve controls the current configuration rejects.
    this._useLegacyFallback = this._aeppic.Features.getOptionsFor('control-lookup')?.fallback === true
  }

  /** Adopt the precomputed folder chains. */
  primeChains(tables: ControlLookupTables) {
    if (!tables?.controlFolderChainByForm) {
      return
    }

    this._folderChainByForm = tables.controlFolderChainByForm
  }

  /**
   * Index the shipped control corpus by the folder holding each control.
   *
   * Sorted by `created.at` ascending per folder, because the server query sorts that way and
   * takes the first result: with the candidates in the same order, "oldest wins" needs no
   * comparison at match time. Documents of any other form are ignored, so the bundle's ancestor
   * closure does not pollute the index.
   */
  primeDocuments(controlDocuments: Types.Document[]) {
    if (!controlDocuments) {
      return
    }

    const candidatesByFolder = new Map<Types.DocumentId, ControlCandidate[]>()

    for (const document of controlDocuments) {
      if (document?.f?.id !== CONTROL_FORM || !document.p) {
        continue
      }

      const candidates = candidatesByFolder.get(document.p) ?? []

      candidates.push(buildCandidate(document))
      candidatesByFolder.set(document.p, candidates)
    }

    for (const candidates of candidatesByFolder.values()) {
      candidates.sort((first, second) => first.createdAt.localeCompare(second.createdAt))
    }

    // An index built from documents but holding none of them resolves everything to nothing, and
    // it would do it silently and without a request - the one failure that looks exactly like
    // success from the outside. It happens if the corpus is handed over after something else has
    // consumed the array, which the importer does.
    if (candidatesByFolder.size === 0) {
      this._aeppic.Warn.once('control-lookup:tables', 'empty-index', `Indexed no controls from ${controlDocuments.length} documents. Control resolution will stay on the network.`)
      return
    }

    this._candidatesByFolder = candidatesByFolder
  }

  get isPrimed() {
    return this._folderChainByForm !== null && this._candidatesByFolder !== null
  }

  /**
   * The control a placeholder resolves to, or why it cannot be answered here.
   *
   * Synchronous on purpose: nothing it does may reach the network, and a signature that cannot
   * await is the simplest way to keep it that way.
   */
  resolve(formId: Types.DocumentId, field: ParsedField|null, controlSelector: string, disableDefaultControl?: boolean): ControlResolution {
    if (!this.isPrimed) {
      return undefined
    }

    const chain = this._folderChainByForm[formId]

    if (!chain) {
      return undefined
    }

    const { namespace, name } = parseSelector(controlSelector)

    // A standalone request without a name has nothing to look for, and without a field there is
    // no type to fall back on either. `findControlDocument` answers null for it too.
    if (!field && name.trim() === '') {
      return null
    }

    const matched = this._findInChain(chain, field, namespace, name.trim() === '' ? field.subType : name)

    if (matched) {
      return matched
    }

    if (!this._useLegacyFallback) {
      return null
    }

    return this._resolveLegacy(controlSelector, field, disableDefaultControl)
  }

  /**
   * Drop everything primed.
   *
   * Any control change can move, rename or remove a candidate, and any folder change can
   * reorder a chain. Patching either in place would have to get every one of those cases right;
   * dropping them falls back to resolving over the network, which is slower for the rest of the
   * session but cannot answer with a control that no longer applies.
   */
  clearCache() {
    this._folderChainByForm = null
    this._candidatesByFolder = null
  }

  /** Walk the chain, first match wins - the same rule as `FormBasedLookup._findThin`. */
  private _findInChain(chain: Types.DocumentId[], field: ParsedField|null, namespace: string, name: string): Types.DocumentId|null {
    for (const folderId of chain) {
      const candidates = this._candidatesByFolder.get(folderId)

      if (!candidates) {
        continue
      }

      for (const candidate of candidates) {
        if (candidate.namespace !== namespace || candidate.name !== name) {
          continue
        }

        if (matchesField(candidate, field)) {
          return candidate.id
        }
      }
    }

    return null
  }

  /** `classicFindControl`, over the same index. */
  private _resolveLegacy(controlSelector: string, field: ParsedField|null, disableDefaultControl?: boolean): ControlResolution {
    const byName = this._findLegacyByName(controlSelector)

    if (byName) {
      return byName
    }

    if (!field || disableDefaultControl) {
      return null
    }

    const byType = this._findLegacyByName(field.subType)

    if (byType) {
      return byType
    }

    // The one control the legacy path resolves by id rather than by search. It is a control
    // document like any other, so the bundle carries it and this stays local.
    return DEFAULT_TEXT_CONTROL
  }

  private _findLegacyByName(controlSelector: string): Types.DocumentId|null {
    if (!controlSelector) {
      return null
    }

    const { namespace, name } = parseLegacySelector(controlSelector)

    for (const folderId of LEGACY_CONTROL_FOLDERS) {
      const candidates = this._candidatesByFolder.get(folderId) ?? []

      // Two passes per folder before moving on, as `lookupControlInLocation` does: the
      // requested namespace first, then `default`.
      const match = candidates.find(candidate => matchesLegacyName(candidate, namespace, name))
                 ?? candidates.find(candidate => matchesLegacyName(candidate, DEFAULT_NAMESPACE, name))

      if (match) {
        return match.id
      }
    }

    return null
  }
}
